/**
 * SOVRANOVA — Affirmations View
 * Personalised self-affirmations. Read them, or play them aloud one by one
 * with calm pacing. Respects the global voice/text settings.
 */
import { useState, useEffect, useRef, useCallback } from 'react'
import { api } from '../../services/api'
import { useSettings } from '../../hooks/useSettings'
import { useWakeLock } from '../../hooks/useSessionResilience'

const THEMES = [
  { id: 'general',     label: 'For Today',      emoji: '🌅' },
  { id: 'self-worth',  label: 'Self-Worth',     emoji: '💗' },
  { id: 'calm',        label: 'Calm & Safety',  emoji: '🌿' },
  { id: 'confidence',  label: 'Confidence',     emoji: '✨' },
  { id: 'letting go',  label: 'Letting Go',     emoji: '🍃' },
]

async function speak(text, audioRef) {
  try {
    const res = await api.post('/voice/speak', { text })
    if (!res?.audio) return
    const bin = atob(res.audio)
    const bytes = new Uint8Array(bin.length)
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i)
    const blob = new Blob([bytes], { type: res.mimeType || 'audio/mpeg' })
    const audio = new Audio(URL.createObjectURL(blob))
    audioRef.current?.pause()
    audioRef.current = audio
    return new Promise(resolve => {
      audio.onended = resolve
      audio.onerror = resolve
      audio.play().catch(resolve)
    })
  } catch { /* silent */ }
}

export function AffirmationsView({ onClose, onHome }) {
  const { settings } = useSettings()
  const [theme, setTheme]   = useState('general')
  const [items, setItems]   = useState([])
  const [loading, setLoad]  = useState(true)
  const [playing, setPlaying] = useState(false)
  // Screen must not sleep mid-playback — a timeout suspends the audio on
  // mobile and the run dies silently (active-use rule, 19 Aug).
  useWakeLock(playing)
  const [activeIdx, setActiveIdx] = useState(-1)
  const audioRef = useRef(null)
  const stopRef  = useRef(false)

  const load = useCallback((t) => {
    setLoad(true); setItems([]); setActiveIdx(-1)
    api.post('/affirmations/generate', { theme: t, count: 8 })
      .then(({ affirmations }) => setItems(affirmations || []))
      .catch(() => setItems([]))
      .finally(() => setLoad(false))
  }, [])

  useEffect(() => { load(theme) }, [theme, load])
  useEffect(() => () => { stopRef.current = true; audioRef.current?.pause() }, [])

  const playAll = useCallback(async () => {
    if (settings.textOnly || !settings.voiceEnabled) return
    setPlaying(true); stopRef.current = false
    for (let i = 0; i < items.length; i++) {
      if (stopRef.current) break
      setActiveIdx(i)
      await speak(items[i], audioRef)
      if (stopRef.current) break
      await new Promise(r => setTimeout(r, 2500)) // calm gap between affirmations
    }
    setActiveIdx(-1); setPlaying(false)
  }, [items, settings])

  const stopPlay = () => { stopRef.current = true; audioRef.current?.pause(); setPlaying(false); setActiveIdx(-1) }

  const voiceAvailable = !settings.textOnly && settings.voiceEnabled

  return (
    <div style={s.page}>
      <div style={s.header}>
        <button style={s.backBtn} onClick={() => { stopPlay(); onClose?.() }}>&#8592; Back</button>
        <p style={s.title}>AFFIRMATIONS</p>
        <button style={s.homeBtn} onClick={() => { stopPlay(); onHome?.() }}>⌂ Home</button>
      </div>

      <div style={s.themeRow}>
        {THEMES.map(t => (
          <button key={t.id}
            style={{ ...s.themeChip, ...(theme === t.id ? s.themeActive : {}) }}
            onClick={() => { stopPlay(); setTheme(t.id) }}>
            {t.emoji} {t.label}
          </button>
        ))}
      </div>

      <div style={s.body}>
        {loading && (
          <div style={s.centre}><div style={s.spinner} /><p style={s.loadingTxt}>Creating affirmations just for you...</p></div>
        )}
        {!loading && items.map((a, i) => (
          <div key={i} style={{ ...s.card, ...(activeIdx === i ? s.cardActive : {}) }}>
            <p style={s.affirmText}>{a}</p>
            {voiceAvailable && (
              <button style={s.playOne} onClick={() => { stopRef.current = false; setActiveIdx(i); speak(a, audioRef).then(() => setActiveIdx(-1)) }}>🔊</button>
            )}
          </div>
        ))}
        {!loading && items.length === 0 && (
          <p style={s.loadingTxt}>Could not load affirmations. Try another theme.</p>
        )}
      </div>

      {!loading && items.length > 0 && voiceAvailable && (
        <div style={s.controls}>
          {playing
            ? <button style={s.stopBtn} onClick={stopPlay}>&#9632; Stop</button>
            : <button style={s.playBtn} onClick={playAll}>▶ Play all aloud</button>}
        </div>
      )}
      {!loading && items.length > 0 && !voiceAvailable && (
        <p style={s.textOnlyNote}>Voice is off (text-only mode). Read each affirmation slowly, breathing between them.</p>
      )}
    </div>
  )
}

const s = {
  // Organic restyle (2026-08-14): the same warm-ink quiet room as the guided
  // players — cold purple replaced, terracotta action, legible cream text.
  // Values only; playback behavior and text-only handling untouched.
  page:      { display:'flex', flexDirection:'column', height:'100%', background:'linear-gradient(170deg,#2e2b25,#211e19)' },
  header:    { display:'flex', alignItems:'center', justifyContent:'space-between', padding:'0.875rem 1.25rem', borderBottom:'1px solid rgba(245,234,216,.08)', background:'transparent', flexShrink:0 },
  backBtn:   { background:'rgba(245,234,216,.08)', border:'none', color:'rgba(245,234,216,.8)', fontSize:'0.78rem', fontWeight:600, cursor:'pointer', borderRadius:'999px', padding:'0.35rem 0.8rem', fontFamily:'inherit' },
  homeBtn:   { background:'rgba(245,234,216,.08)', border:'none', color:'rgba(245,234,216,.7)', borderRadius:'999px', padding:'0.35rem 0.8rem', fontSize:'0.78rem', cursor:'pointer', fontWeight:600, fontFamily:'inherit' },
  title:     { fontSize:'0.72rem', fontWeight:700, letterSpacing:'0.14em', color:'rgba(245,234,216,.65)' },
  themeRow:  { display:'flex', gap:'0.5rem', padding:'0.875rem 1.25rem', overflowX:'auto', flexShrink:0, borderBottom:'1px solid rgba(245,234,216,.06)' },
  themeChip: { padding:'0.45rem 0.9rem', background:'rgba(245,234,216,.05)', border:'none', borderRadius:'999px', color:'rgba(245,234,216,.65)', fontSize:'0.8rem', cursor:'pointer', whiteSpace:'nowrap', flexShrink:0, fontFamily:'inherit' },
  themeActive:{ background:'rgba(198,113,57,.3)', border:'none', color:'#ffe1d0', fontWeight:600 },
  body:      { flex:1, overflowY:'auto', padding:'1.25rem', display:'flex', flexDirection:'column', gap:'0.75rem' },
  centre:    { flex:1, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:'1rem', paddingTop:'3rem' },
  spinner:   { width:36, height:36, border:'2px solid rgba(198,113,57,.25)', borderTop:'2px solid #c67139', borderRadius:'50%', animation:'spin 1s linear infinite' },
  loadingTxt:{ fontSize:'0.9rem', color:'rgba(245,234,216,.5)', textAlign:'center', fontStyle:'italic' },
  card:      { display:'flex', alignItems:'center', gap:'0.75rem', background:'rgba(245,234,216,.05)', border:'none', borderRadius:'20px', padding:'1.1rem 1.25rem', transition:'all .3s' },
  cardActive:{ background:'rgba(198,113,57,.18)', border:'none', transform:'scale(1.02)' },
  affirmText:{ flex:1, fontSize:'1.05rem', color:'#f5ead8', lineHeight:1.6, fontStyle:'italic' },
  playOne:   { background:'transparent', border:'none', fontSize:'1.1rem', cursor:'pointer', opacity:0.6, flexShrink:0 },
  controls:  { padding:'0.875rem 1.25rem 1.5rem', flexShrink:0 },
  playBtn:   { width:'100%', padding:'1rem', background:'#c67139', color:'#f5ead8', border:'none', borderRadius:'999px', fontSize:'1rem', fontWeight:600, cursor:'pointer', fontFamily:'inherit' },
  stopBtn:   { width:'100%', padding:'1rem', background:'rgba(245,234,216,.06)', border:'none', color:'rgba(245,234,216,.6)', borderRadius:'999px', fontSize:'1rem', cursor:'pointer', fontFamily:'inherit' },
  textOnlyNote: { padding:'0 1.25rem 1.5rem', fontSize:'0.78rem', color:'rgba(245,234,216,.45)', textAlign:'center', lineHeight:1.6 },
}

if (typeof document !== 'undefined' && !document.getElementById('sv-affirm-anim')) {
  const el = document.createElement('style'); el.id = 'sv-affirm-anim'
  el.textContent = '@keyframes spin { to { transform: rotate(360deg) } }'
  document.head.appendChild(el)
}
